import { Divider, List, Text } from "@ui-kitten/components";
import Chevron from "components/Chevron";
import { LIGHT_BLUE } from "constants/";
import {
  BestTicketBlockDetails,
  SeatDetails,
  SectionDetails,
  TenantOptionsDetails,
} from "features/types";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Pressable, View } from "react-native";
import { rankScore } from "../helpers";
import { FiltersInterface } from "../types";
import SectionItem from "./SectionItem";

interface Props {
  sections?: SectionDetails[];
  bestBlocks?: BestTicketBlockDetails[];
  selectingBestBlock: boolean;
  height: number;
  handleSectionPress: (section: SectionDetails) => void;
  handleBestBlockPress: (tickets: SeatDetails[]) => void;
  onBackBtnPress: () => void;
  shouldShowGuestPrice: boolean;
  ticketProvider: TenantOptionsDetails["ticketProvider"];
  seatSelectionStep: number;
  allBlocks: BestTicketBlockDetails[];
  filters?: FiltersInterface;
}

const ListEmptyComponent = (
  <Text category="h6" style={{ textAlign: "center" }}>
    {i18n.t("screens.SeatSelection.noSeats")}
  </Text>
);

const SectionList: React.FC<Props> = (props) => {
  const {
    sections = [],
    bestBlocks = [],
    allBlocks = [],
    selectingBestBlock,
    height,
    handleSectionPress,
    handleBestBlockPress,
    onBackBtnPress,
    shouldShowGuestPrice,
    ticketProvider,
    seatSelectionStep,
    filters,
  } = props;
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { hideSeatRatings } = tenantConfig!;

  const BackToSectionsButton = () => (
    <Pressable
      style={{
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "white",
      }}
      onPress={onBackBtnPress}
    >
      <Chevron direction="left" color={LIGHT_BLUE} />
      <Text
        style={{
          color: LIGHT_BLUE,
        }}
      >
        {i18n.t("screens.SeatSelection.backToSections")}
      </Text>
    </Pressable>
  );

  const ListHeaderComponent = () => (
    <View
      style={{
        backgroundColor: "white",
        paddingVertical: 5,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {ticketProvider !== "CAPTURED" && seatSelectionStep === 2 && (
        <BackToSectionsButton />
      )}
      <Text
        style={{
          textAlign: "right", // added here so even if back to sections button is not present, the text is still aligned right
          fontWeight: "700",
          marginLeft: "auto",
          marginRight: 30,
        }}
      >
        {shouldShowGuestPrice
          ? i18n.t("screens.SeatSelection.membersAndGuests")
          : i18n.t("screens.SeatSelection.members")}
      </Text>
    </View>
  );
  const renderItem = (
    item: BestTicketBlockDetails | SectionDetails,
    index: number
  ) => {
    const blocks = selectingBestBlock ? allBlocks : sections;
    return (
      <SectionItem
        item={item}
        selectingBestBlock={selectingBestBlock}
        hideRating={hideSeatRatings}
        rs={rankScore(blocks, item.quality, item, "score") as number}
        color={rankScore(blocks, item.quality, item, "color") as string}
        msg={rankScore(blocks, item.quality, item, "message") as string}
        shouldShowGuestPrice={shouldShowGuestPrice}
        includeAccessible={filters?.showAccessible || false}
        handleBestBlockPress={handleBestBlockPress}
        handleSectionPress={handleSectionPress}
        positionInList={index}
      />
    );
  };

  return (
    <View>
      {selectingBestBlock ? (
        <List
          ListEmptyComponent={ListEmptyComponent}
          data={bestBlocks}
          keyExtractor={(item, index) => item.section + item.row + index}
          style={{ height, backgroundColor: "transparent" }}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={ListHeaderComponent}
          renderItem={({ item, index }) => renderItem(item, index)}
          ItemSeparatorComponent={() => <Divider />}
        />
      ) : (
        <List
          ListEmptyComponent={ListEmptyComponent}
          data={sections}
          keyExtractor={(item, index) => item.section + index}
          style={{ height, backgroundColor: "transparent" }}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={ListHeaderComponent}
          renderItem={({ item, index }) => renderItem(item, index)}
          ItemSeparatorComponent={() => <Divider />}
        />
      )}
    </View>
  );
};

export default SectionList;
